import React, { useState, useMemo, useEffect, Suspense, lazy } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { Container, useMediaQuery, CircularProgress, Box } from "@mui/material";
import { DataProvider } from "./contexts/DataContext";
import { getDesignTokens, getThemedComponents } from "./conf/theme.ts";
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { ColorModeContext } from "./contexts/ColorModeContext";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import SecureRoute from "./components/SecureRoute.js";
import AuthCallback from "./components/AuthCallback.js";
import InvitePage from "./pages/Invite.js";
import InterestPage from "./pages/Interest.js";
import sharedConfig from "../shared-config.json";
import ResponsiveAppBar from "./components/appBar";
import LoginDialog from "./components/LoginDialog";
import CachedUploadsNotification from "./components/CachedUploadsNotification";

// Routes that use their own header (public-facing pages)
const PUBLIC_ROUTES = ["/", "/register", "/participant-register", "/consent-return", "/login", "/contact", "/faq", "/invite", "/book", "/interest"];

function AppShell({ loginOpen, handleLoginClick, handleLoginClose }) {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  return (
    <>
      {!isPublicRoute && <ResponsiveAppBar onLoginClick={handleLoginClick} />}
      <LoginDialog open={loginOpen} onClose={handleLoginClose} />
      <CachedUploadsNotification />
    </>
  );
}

// Only use code splitting for major route-based components
const Home = lazy(() => import(/* webpackChunkName: "home" */ "./pages/Home"));
const Participant = lazy(() =>
  import(/* webpackChunkName: "participant" */ "./pages/Participant")
);
const ParticipantRegister = lazy(() =>
  import(/* webpackChunkName: "participant" */ "./pages/ParticipantRegister")
);
const Admin = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/Admin")
);
const ConsentReturn = lazy(() =>
  import(/* webpackChunkName: "enrollment" */ "./pages/ConsentReturn")
);
const Book = lazy(() => import(/* webpackChunkName: "book" */ "./pages/Book"));
// Loading component for suspense fallback
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "calc(100vh - 64px)",
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [mode, setMode] = useState("light");
  const [loginOpen, setLoginOpen] = useState(false);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    setMode("light");
    //setMode(prefersDarkMode ? "dark" : "light");
  }, [prefersDarkMode]);

  let theme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme(deepmerge(getDesignTokens(mode), getThemedComponents(mode)))
      ),
    [mode]
  );

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const handleLoginClick = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <DataProvider>
          <BrowserRouter>
            <AppShell
              loginOpen={loginOpen}
              handleLoginClick={handleLoginClick}
              handleLoginClose={handleLoginClose}
            />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Admin/PI routes */}
                <Route
                  path="/admin"
                  element={
                    <SecureRoute
                      roleRequired={["PRADAM.Admin", "PRADAM.PI"]}
                      element={<Admin />}
                    />
                  }
                />
                {/* /admin/studies route retired — study config now lives
                    as a tab on /admin. See Admin.js → StudySettings. */}

                {/* Public /contact and /faq routes retired — content
                    folded into Home. */}
                <Route
                  path="/"
                  element={<Home onLoginClick={handleLoginClick} />}
                />
                <Route
                  path="/login"
                  element={
                    <Navigate to="/" state={{ showLogin: true }} replace />
                  }
                />

                {/* Participant routes */}
                <Route
                  path="/participant"
                  element={
                    <SecureRoute
                      roleRequired={[sharedConfig.roles.participant.name]}
                      element={<Participant />}
                      excludeRoles={[
                        sharedConfig.roles.admin.name,
                        sharedConfig.roles.pi.name,
                      ]}
                    />
                  }
                />
                <Route path="/register" element={<ParticipantRegister />} />
                <Route
                  path="/participant-register"
                  element={<ParticipantRegister />}
                />

                {/* Enrollment flow routes */}
                <Route path="/consent-return" element={<ConsentReturn />} />

                {/* Invite redemption page — branded, hosted on pradam.org.
                    Email invite links land here; Zitadel is only touched
                    via OIDC redirect for the Google/Microsoft path. */}
                <Route
                  path="/invite"
                  element={<InvitePage onLoginClick={handleLoginClick} />}
                />

                {/* Screening-call booking — public, reached from the REDCap
                    EOI completion redirect (/book?eoi=[record-id]). */}
                <Route
                  path="/book"
                  element={<Book onLoginClick={handleLoginClick} />}
                />

                {/* Link for adverts: straight on to the EOI form with a
                    booking key, keeping the advert's utm_ values. */}
                <Route path="/interest" element={<InterestPage />} />

                {/* OIDC callback — must render long enough for MsalContext
                    to exchange the code for a token before redirecting. */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/silent-renew" element={<AuthCallback />} />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </DataProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
